import { useMemo, useState } from "react";
import { Banknote, CalendarDays, PackageCheck, RotateCcw, ShoppingBag, Store, TrendingUp, UsersRound } from "lucide-react";
import { Card, EmptyState, PageHeader } from "../components/ui";
import type { Customer, Order } from "../types";
import { ORDER_STATUSES } from "../types";
import { money, orderItems, toDate } from "../utils";

const DAY = 86_400_000;

export function AnalyticsPage({ orders, customers }: { orders: Order[]; customers: Customer[] }) {
  const today = startOfDay(new Date());
  const [from, setFrom] = useState(dateInput(new Date(today.getTime() - 29 * DAY)));
  const [to, setTo] = useState(dateInput(today));
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T23:59:59.999`);
  const periodOrders = useMemo(() => orders.filter((order) => {
    const created = toDate(order.createdAt);
    return created && created >= start && created <= end;
  }), [end.getTime(), orders, start.getTime()]);

  const validOrders = periodOrders.filter((order) => ![8, 9].includes(order.orderStatus ?? 0));
  const delivered = periodOrders.filter((order) => (order.orderStatus ?? 0) === 7);
  const returned = periodOrders.filter((order) => (order.orderStatus ?? 0) === 9);
  const cancelled = periodOrders.filter((order) => (order.orderStatus ?? 0) === 8);
  const pickup = periodOrders.filter((order) => order.deliveryMethod === "store_pickup");
  const gross = sumOrders(validOrders);
  const collectedCash = sumOrders(delivered);
  const returnedValue = sumOrders(returned);
  const units = validOrders.reduce((sum, order) => sum + orderItems(order).reduce((itemSum, item) => itemSum + Math.max(1, item.quantity ?? 1), 0), 0);
  const average = validOrders.length ? gross / validOrders.length : 0;
  const returnRate = periodOrders.length ? returned.length / periodOrders.length * 100 : 0;
  const pickupShare = periodOrders.length ? pickup.length / periodOrders.length * 100 : 0;
  const daily = dailySeries(periodOrders, start, end);
  const maxDaily = Math.max(1, ...daily.map((entry) => entry.revenue));
  const topProducts = productRows(validOrders).slice(0, 8);
  const topCustomers = customerRows(validOrders, customers).slice(0, 6);

  const preset = (days: number) => {
    const nextToday = startOfDay(new Date());
    setTo(dateInput(nextToday));
    setFrom(dateInput(new Date(nextToday.getTime() - (days - 1) * DAY)));
  };

  return <>
    <PageHeader title="Анализи" description="Пълна статистика за избрания период, базирана на поръчките в реално време." />
    <Card className="analytics-filter-card">
      <div className="analytics-filter">
        <span className="analytics-filter-icon"><CalendarDays size={20} /></span>
        <label>От<input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>До<input type="date" value={to} min={from} max={dateInput(today)} onChange={(event) => setTo(event.target.value)} /></label>
        <div className="preset-buttons"><button onClick={() => preset(7)}>7 дни</button><button onClick={() => preset(30)}>30 дни</button><button onClick={() => preset(90)}>90 дни</button><button onClick={() => { setFrom(`${today.getFullYear()}-01-01`); setTo(dateInput(today)); }}>Тази година</button></div>
        <strong>{periodOrders.length} поръчки</strong>
      </div>
    </Card>

    <section className="analytics-metrics">
      <AnalyticsMetric label="Поръчки" value={String(periodOrders.length)} note={`${units} продадени артикула`} icon={ShoppingBag} tone="purple" />
      <AnalyticsMetric label="Оборот" value={money(gross)} note={`Средна поръчка ${money(average)}`} icon={TrendingUp} tone="blue" />
      <AnalyticsMetric label="Събрани пари" value={money(collectedCash)} note={`${delivered.length} завършени поръчки`} icon={Banknote} tone="green" />
      <AnalyticsMetric label="Върнати" value={String(returned.length)} note={`${money(returnedValue)} · ${returnRate.toFixed(1)}%`} icon={RotateCcw} tone="red" />
      <AnalyticsMetric label="Отказани" value={String(cancelled.length)} note={money(sumOrders(cancelled))} icon={PackageCheck} tone="amber" />
      <AnalyticsMetric label="Вземане от магазин" value={String(pickup.length)} note={`${pickupShare.toFixed(1)}% от поръчките`} icon={Store} tone="cyan" />
    </section>

    <section className="analytics-grid">
      <Card title="Оборот по дни" action={<span className="muted">{formatShort(start)} — {formatShort(end)}</span>}>
        {daily.length === 0 ? <EmptyState title="Няма данни" body="За избрания период няма поръчки." /> : <div className="revenue-chart">{daily.map((entry) => <div className="revenue-column" key={entry.key} title={`${entry.label}: ${money(entry.revenue)} · ${entry.orders} поръчки`}><div className="revenue-bar-track"><span style={{ height: `${Math.max(entry.revenue > 0 ? 5 : 0, entry.revenue / maxDaily * 100)}%` }} /></div><strong>{entry.short}</strong><small>{entry.orders}</small></div>)}</div>}
      </Card>
      <Card title="Статуси на поръчките">
        <div className="status-breakdown">{ORDER_STATUSES.map((status) => { const count = periodOrders.filter((order) => (order.orderStatus ?? 0) === status.value).length; const width = periodOrders.length ? count / periodOrders.length * 100 : 0; return <div key={status.value}><span><strong>{status.label}</strong><small>{count} · {width.toFixed(1)}%</small></span><div><i className={status.tone} style={{ width: `${width}%` }} /></div></div>; })}</div>
      </Card>
    </section>

    <section className="analytics-grid bottom">
      <Card title="Най-продавани продукти">
        {topProducts.length === 0 ? <EmptyState title="Няма продажби" body="Продуктите ще се покажат след първата поръчка." /> : <div className="analytics-table"><table><thead><tr><th>Продукт</th><th>Бройки</th><th>Оборот</th></tr></thead><tbody>{topProducts.map((item) => <tr key={item.id}><td><strong>{item.name}</strong></td><td>{item.quantity}</td><td><strong>{money(item.revenue)}</strong></td></tr>)}</tbody></table></div>}
      </Card>
      <Card title="Най-активни клиенти" action={<span className="analytics-card-icon"><UsersRound size={17} /></span>}>
        {topCustomers.length === 0 ? <EmptyState title="Няма клиенти" body="Клиентската статистика ще се появи с поръчките." /> : <div className="top-customer-list">{topCustomers.map((item, index) => <div key={item.id}><span>{index + 1}</span><p><strong>{item.name}</strong><small>{item.orders} поръчки</small></p><b>{money(item.spent)}</b></div>)}</div>}
      </Card>
    </section>
  </>;
}

function AnalyticsMetric({ label, value, note, icon: Icon, tone }: { label: string; value: string; note: string; icon: typeof Banknote; tone: string }) {
  return <article className="analytics-metric"><span className={`metric-icon ${tone}`}><Icon size={20} /></span><p>{label}</p><strong>{value}</strong><small>{note}</small></article>;
}

function dailySeries(orders: Order[], start: Date, end: Date) {
  const dayCount = Math.max(1, Math.floor((end.getTime() - start.getTime()) / DAY) + 1);
  const includeEmptyDays = dayCount <= 62;
  const rows = new Map<string, { key: string; label: string; short: string; revenue: number; orders: number }>();
  if (includeEmptyDays) {
    for (let cursor = startOfDay(start); cursor <= end; cursor = new Date(cursor.getTime() + DAY)) {
      const key = dateInput(cursor);
      rows.set(key, { key, label: formatShort(cursor), short: cursor.toLocaleDateString("bg-BG", { day: "2-digit", month: "2-digit" }), revenue: 0, orders: 0 });
    }
  }
  orders.forEach((order) => {
    const date = toDate(order.createdAt);
    if (!date) return;
    const key = dateInput(date);
    const row = rows.get(key) ?? { key, label: formatShort(date), short: date.toLocaleDateString("bg-BG", { day: "2-digit", month: "2-digit" }), revenue: 0, orders: 0 };
    row.orders++;
    if (![8, 9].includes(order.orderStatus ?? 0)) row.revenue += order.totalPrice ?? 0;
    rows.set(key, row);
  });
  return [...rows.values()].sort((left, right) => left.key.localeCompare(right.key));
}

function productRows(orders: Order[]) {
  const rows = new Map<string, { id: string; name: string; quantity: number; revenue: number }>();
  orders.forEach((order) => orderItems(order).forEach((item) => {
    const id = item.productId || item.productName || "unknown";
    const quantity = Math.max(1, item.quantity ?? 1);
    const row = rows.get(id) ?? { id, name: item.productName || item.productData?.name || "Продукт", quantity: 0, revenue: 0 };
    row.quantity += quantity;
    row.revenue += (item.unitPrice ?? item.productData?.price ?? 0) * quantity;
    rows.set(id, row);
  }));
  return [...rows.values()].sort((left, right) => right.quantity - left.quantity || right.revenue - left.revenue);
}

function customerRows(orders: Order[], customers: Customer[]) {
  const rows = new Map<string, { id: string; name: string; orders: number; spent: number }>();
  orders.forEach((order) => {
    const id = order.userId || order.email || order.phone || order.id;
    const customer = customers.find((item) => item.id === order.userId || (!!order.email && item.email === order.email));
    const row = rows.get(id) ?? { id, name: order.name || customer?.username || order.email || "Клиент", orders: 0, spent: 0 };
    row.orders++;
    row.spent += order.totalPrice ?? 0;
    rows.set(id, row);
  });
  return [...rows.values()].sort((left, right) => right.spent - left.spent);
}

const sumOrders = (orders: Order[]) => orders.reduce((sum, order) => sum + (order.totalPrice ?? 0), 0);
const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const dateInput = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const formatShort = (date: Date) => date.toLocaleDateString("bg-BG", { day: "2-digit", month: "short", year: "numeric" });
